/** Design tokens from the brand handoff. Nothing here is invented. */

export const c = {
  /** Off-white behind the phone frame. */
  page: '#FAF8F3',
  /** The app screen itself — the 390px frame and sheets. */
  screen: '#EEEEEE',
  /** The surface a component sits on — a section card sets --rb-surface to its own grey. */
  surface: 'var(--rb-surface, #EEEEEE)',
  /** Overview section cards below the hero, each a step darker. */
  section1: '#E3E3E3',
  section2: '#D8D8D8',
  section3: '#CCCCCC',
  /** Light text and marks on dark or coloured fills. */
  cream: '#F5EEDB',
  /** Panels and tiles: the warm tint over the screen grey. */
  creamDeep: 'rgba(140,110,100,0.08)',
  tint: 'rgba(140,110,100,0.08)',
  tintOpen: 'rgba(140,110,100,0.13)',
  tintHover: 'rgba(140,110,100,0.16)',
  field: '#FAF8F3',
  ink: '#1F1A1F',
  ink75: 'rgba(31,26,31,0.75)',
  ink6: 'rgba(31,26,31,0.6)',
  ink55: 'rgba(31,26,31,0.55)',
  ink5: 'rgba(31,26,31,0.5)',
  ink45: 'rgba(31,26,31,0.45)',
  ink4: 'rgba(31,26,31,0.4)',
  ink25: 'rgba(31,26,31,0.25)',
  ink18: 'rgba(31,26,31,0.18)',
  hairline: 'rgba(31,26,31,0.12)',
  hairlineList: 'rgba(31,26,31,0.1)',
  chip: 'rgba(31,26,31,0.06)',
  red: '#D63A2E',
  redDark: '#B02A20',
  redLine: 'rgba(214,58,46,0.5)',
  redSoft: 'rgba(214,58,46,0.14)',
  magenta: '#E22A8C',
  violet: '#6A2BD9',
  cyan: '#2BB3E6',
  lime: '#E3F03C',
} as const;

/** Status tones for module rows. */
export const tone = {
  ok: '#3A3BD6',
  strong: '#1E8A5A',
  warn: '#C9741A',
  high: '#D63A2E',
  neutral: 'rgba(31,26,31,0.55)',
  cyan: '#2BB3E6',
  pink: '#E22A8C',
} as const;

export type ToneKey = keyof typeof tone;

/**
 * Letter-spacing adds a trailing space after the last character, and that space
 * counts in the text's width — so centred tracked text sits half a letter-space
 * left of true centre. These put it back.
 *
 * `tracked` is for an element that shrinks to its content (a flex child under
 * `align-items: center`); `trackedBlock` is for full-width text centred with
 * `text-align: center`.
 */
export const tracked = (em: number): { letterSpacing: string; marginRight: string } => ({
  letterSpacing: `${em}em`,
  marginRight: `-${em}em`,
});

export const trackedBlock = (em: number): { letterSpacing: string; paddingLeft: string } => ({
  letterSpacing: `${em}em`,
  paddingLeft: `${em}em`,
});

export const rgbaOf = (hex: string, alpha: number): string => {
  if (!hex.startsWith('#')) return `rgba(31,26,31,${alpha})`;
  const n = hex.slice(1);
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

/** $1.88m / $940k — the short form the ring and tiles use. */
export const fmtShort = (n: number | null | undefined): string => {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}m`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}k`;
  return `$${Math.round(n)}`;
};

/**
 * The price cells print a million split from its unit: $2.01 · M
 *
 * Two decimals, matching `fmtShort`, because the two render the same figure on
 * the same screen — the ring said $2.01m while the cell below it said $2.008 M,
 * and one number shown twice has to be shown the same way.
 */
export const millions = (n: number | null | undefined): { value: string; unit: string } => {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return { value: '—', unit: '' };
  if (n >= 1_000_000) return { value: `$${(n / 1_000_000).toFixed(2)}`, unit: 'M' };
  return { value: `$${Math.round(n / 1000)}`, unit: 'K' };
};

/**
 * The portfolio prints estimates to a fixed two decimals — $3.86m, $1.88m —
 * so a column of cards lines up. Unlike fmtShort, zeros are kept.
 */
export const fmtEstimate = (n: number | null | undefined): string => {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}m`;
  return `$${Math.round(n / 1000)}k`;
};

/** A report is valid for 30 days; the card counts down from its build date. */
export const validityDays = (iso: string, now = new Date()): number => {
  const built = new Date(iso).getTime();
  if (Number.isNaN(built)) return 0;
  const elapsed = Math.floor((now.getTime() - built) / 86_400_000);
  return Math.max(0, 30 - elapsed);
};

export const fmtDate = (iso: string): string =>
  new Date(iso)
    .toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
    .toUpperCase()
    .replace(/\./g, '');
